/**
 * useI18nNavigate — wraps react-router navigate() with the current language prefix.
 *
 * Usage:
 *   const navigate = useI18nNavigate()
 *   navigate('/search')          // → /search (en) or /ar/search (ar)
 *
 * useLangPath — converts a bare path to a language-prefixed path.
 * Useful for <Link to={langPath('/login')}>
 *
 * useLanguageSwitcher — switches the active language by replacing the URL prefix.
 */
import { useCallback } from 'react'
import { useNavigate, useLocation, useParams, type NavigateOptions } from 'react-router-dom'
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n'
import i18n from '../i18n'

// ── Derive language from URL params ───────────────────────────────────────────

/**
 * Returns the active language from the URL /:lang param, or 'en' if absent/invalid.
 */
export function useActiveLang(): SupportedLanguage {
  const { lang } = useParams<{ lang?: string }>()
  if (lang && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
    return lang as SupportedLanguage
  }
  return 'en'
}

// ── Build a language-prefixed path ────────────────────────────────────────────

function buildLangPath(lang: SupportedLanguage, path: string): string {
  const prefix = lang === 'en' ? '' : `/${lang}`
  // Ensure the path starts with /
  const normalised = path.startsWith('/') ? path : `/${path}`
  return `${prefix}${normalised}`
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Returns a navigate function that automatically prepends the current language
 * prefix. Pass `newLang` to switch language during navigation.
 */
export function useI18nNavigate() {
  const navigate = useNavigate()
  const lang = useActiveLang()

  return useCallback(
    (to: string, options?: NavigateOptions & { lang?: SupportedLanguage }) => {
      const targetLang = options?.lang ?? lang
      navigate(buildLangPath(targetLang, to), options)
    },
    [navigate, lang]
  )
}

/**
 * Returns a helper that converts a bare path to a language-prefixed path.
 * Useful for `<Link to={langPath('/login')}>`.
 */
export function useLangPath() {
  const lang = useActiveLang()
  return useCallback(
    (path: string) => buildLangPath(lang, path),
    [lang]
  )
}

/**
 * Returns the current active language and a function to switch it.
 * Switches by replacing the language prefix in the current URL.
 */
export function useLanguageSwitcher() {
  const navigate = useNavigate()
  const location = useLocation()
  const { lang: urlLang } = useParams<{ lang?: string }>()
  const currentLang = useActiveLang()

  const switchLanguage = useCallback(
    (newLang: SupportedLanguage) => {
      // Strip current lang prefix from the path
      let barePath = location.pathname
      if (urlLang && barePath.startsWith(`/${urlLang}`)) {
        barePath = barePath.slice(urlLang.length + 1) // remove /lang
      }
      if (!barePath.startsWith('/')) barePath = `/${barePath}`
      if (barePath === '') barePath = '/'

      // Apply i18n + document direction immediately (before navigation resolves)
      i18n.changeLanguage(newLang)
      document.documentElement.lang = newLang
      document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr'
      localStorage.setItem('i18n_lang', newLang)

      navigate(buildLangPath(newLang, barePath), { replace: true })
    },
    [navigate, location.pathname, urlLang]
  )

  return { currentLang, switchLanguage }
}
